import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query'
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query'
import axios from 'axios'
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios'
import type {
  CreateRoadmap400,
  CreateRoadmap401,
  CreateRoadmap500,
  CreateRoadmapBody,
  DeleteRoadmap401,
  DeleteRoadmap403,
  DeleteRoadmap404,
  DeleteRoadmap500,
  GetRoadmapDetail200,
  GetRoadmapDetail404,
  GetRoadmapDetail500,
  GetRoadmaps200,
  GetRoadmaps400,
  GetRoadmaps500,
  GetRoadmapsParams,
  SubscribeRoadmap401,
  SubscribeRoadmap403,
  SubscribeRoadmap404,
  SubscribeRoadmap409,
  SubscribeRoadmap500,
  UnsubscribeRoadmap401,
  UnsubscribeRoadmap403,
  UnsubscribeRoadmap404,
  UnsubscribeRoadmap500,
  UpdateRoadmap400,
  UpdateRoadmap401,
  UpdateRoadmap403,
  UpdateRoadmap404,
  UpdateRoadmap500,
  UpdateRoadmapBody
} from './types'


type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;



/**
 * 필터링, 정렬, 페이지네이션을 지원하는 로드맵 목록을 조회합니다.
 * @summary 로드맵 목록 조회
 */
export const getRoadmaps = (
    params?: GetRoadmapsParams, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetRoadmaps200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/roadmaps`,{
    ...options,
        params: {...params, ...options?.params},}
    );
  }


export const getGetRoadmapsQueryKey = (params?: GetRoadmapsParams,) => {
    return [`https://api.ugot.uk/api/roadmaps`, ...(params ? [params]: [])] as const;
    }

    
export const getGetRoadmapsInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getRoadmaps>>, TError = AxiosError<GetRoadmaps400 | GetRoadmaps500>>(params?: GetRoadmapsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getRoadmaps>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRoadmapsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRoadmaps>>> = ({ signal }) => getRoadmaps(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getRoadmaps>>, TError, TData> & { queryKey: QueryKey }
}

export type GetRoadmapsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getRoadmaps>>>
export type GetRoadmapsInfiniteQueryError = AxiosError<GetRoadmaps400 | GetRoadmaps500>

/**
 * @summary 로드맵 목록 조회
 */
export const useGetRoadmapsInfinite = <TData = Awaited<ReturnType<typeof getRoadmaps>>, TError = AxiosError<GetRoadmaps400 | GetRoadmaps500>>(
 params?: GetRoadmapsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getRoadmaps>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetRoadmapsInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetRoadmapsQueryOptions = <TData = Awaited<ReturnType<typeof getRoadmaps>>, TError = AxiosError<GetRoadmaps400 | GetRoadmaps500>>(params?: GetRoadmapsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRoadmaps>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRoadmapsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRoadmaps>>> = ({ signal }) => getRoadmaps(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getRoadmaps>>, TError, TData> & { queryKey: QueryKey }
}

export type GetRoadmapsQueryResult = NonNullable<Awaited<ReturnType<typeof getRoadmaps>>>
export type GetRoadmapsQueryError = AxiosError<GetRoadmaps400 | GetRoadmaps500>

/**
 * @summary 로드맵 목록 조회
 */
export const useGetRoadmaps = <TData = Awaited<ReturnType<typeof getRoadmaps>>, TError = AxiosError<GetRoadmaps400 | GetRoadmaps500>>(
 params?: GetRoadmapsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRoadmaps>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetRoadmapsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * 제목, 설명, 강의 목록을 포함하는 새 로드맵을 생성합니다.
 * @summary 새 로드맵 생성
 */
export const createRoadmap = (
    createRoadmapBody: CreateRoadmapBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<number>> => {
    
    return axios.post(
      `https://api.ugot.uk/api/roadmaps`,
      createRoadmapBody,options
    );
  }



export const getCreateRoadmapMutationOptions = <TError = AxiosError<CreateRoadmap400 | CreateRoadmap401 | CreateRoadmap500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createRoadmap>>, TError,{data: CreateRoadmapBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof createRoadmap>>, TError,{data: CreateRoadmapBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createRoadmap>>, {data: CreateRoadmapBody}> = (props) => {
          const {data} = props ?? {};

          return  createRoadmap(data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateRoadmapMutationResult = NonNullable<Awaited<ReturnType<typeof createRoadmap>>>
    export type CreateRoadmapMutationBody = CreateRoadmapBody
    export type CreateRoadmapMutationError = AxiosError<CreateRoadmap400 | CreateRoadmap401 | CreateRoadmap500>

    /**
 * @summary 새 로드맵 생성
 */
export const useCreateRoadmap = <TError = AxiosError<CreateRoadmap400 | CreateRoadmap401 | CreateRoadmap500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createRoadmap>>, TError,{data: CreateRoadmapBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof createRoadmap>>,
        TError,
        {data: CreateRoadmapBody},
        TContext
      > => {

      const mutationOptions = getCreateRoadmapMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 특정 로드맵의 상세 정보를 조회합니다.
 * @summary 로드맵 상세 조회
 */
export const getRoadmapDetail = (
    roadmapId: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetRoadmapDetail200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/roadmaps/${roadmapId}`,options
    );
  }


export const getGetRoadmapDetailQueryKey = (roadmapId: number,) => {
    return [`https://api.ugot.uk/api/roadmaps/${roadmapId}`] as const;
    }

    
export const getGetRoadmapDetailInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getRoadmapDetail>>, TError = AxiosError<GetRoadmapDetail404 | GetRoadmapDetail500>>(roadmapId: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getRoadmapDetail>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRoadmapDetailQueryKey(roadmapId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRoadmapDetail>>> = ({ signal }) => getRoadmapDetail(roadmapId, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(roadmapId),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getRoadmapDetail>>, TError, TData> & { queryKey: QueryKey }
}

export type GetRoadmapDetailInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getRoadmapDetail>>>
export type GetRoadmapDetailInfiniteQueryError = AxiosError<GetRoadmapDetail404 | GetRoadmapDetail500>

/**
 * @summary 로드맵 상세 조회
 */
export const useGetRoadmapDetailInfinite = <TData = Awaited<ReturnType<typeof getRoadmapDetail>>, TError = AxiosError<GetRoadmapDetail404 | GetRoadmapDetail500>>(
 roadmapId: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getRoadmapDetail>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetRoadmapDetailInfiniteQueryOptions(roadmapId,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetRoadmapDetailQueryOptions = <TData = Awaited<ReturnType<typeof getRoadmapDetail>>, TError = AxiosError<GetRoadmapDetail404 | GetRoadmapDetail500>>(roadmapId: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRoadmapDetail>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRoadmapDetailQueryKey(roadmapId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRoadmapDetail>>> = ({ signal }) => getRoadmapDetail(roadmapId, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(roadmapId),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getRoadmapDetail>>, TError, TData> & { queryKey: QueryKey }
}

export type GetRoadmapDetailQueryResult = NonNullable<Awaited<ReturnType<typeof getRoadmapDetail>>>
export type GetRoadmapDetailQueryError = AxiosError<GetRoadmapDetail404 | GetRoadmapDetail500>

/**
 * @summary 로드맵 상세 조회
 */
export const useGetRoadmapDetail = <TData = Awaited<ReturnType<typeof getRoadmapDetail>>, TError = AxiosError<GetRoadmapDetail404 | GetRoadmapDetail500>>(
 roadmapId: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRoadmapDetail>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetRoadmapDetailQueryOptions(roadmapId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * 특정 로드맵의 정보를 수정합니다. 로드맵 소유자만 수정할 수 있습니다.
 * @summary 로드맵 수정
 */
export const updateRoadmap = (
    roadmapId: number,
    updateRoadmapBody: UpdateRoadmapBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<number>> => {
    
    return axios.put(
      `https://api.ugot.uk/api/roadmaps/${roadmapId}`,
      updateRoadmapBody,options
    );
  }



export const getUpdateRoadmapMutationOptions = <TError = AxiosError<UpdateRoadmap400 | UpdateRoadmap401 | UpdateRoadmap403 | UpdateRoadmap404 | UpdateRoadmap500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateRoadmap>>, TError,{roadmapId: number;data: UpdateRoadmapBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof updateRoadmap>>, TError,{roadmapId: number;data: UpdateRoadmapBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateRoadmap>>, {roadmapId: number;data: UpdateRoadmapBody}> = (props) => {
          const {roadmapId,data} = props ?? {};

          return  updateRoadmap(roadmapId,data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateRoadmapMutationResult = NonNullable<Awaited<ReturnType<typeof updateRoadmap>>>
    export type UpdateRoadmapMutationBody = UpdateRoadmapBody
    export type UpdateRoadmapMutationError = AxiosError<UpdateRoadmap400 | UpdateRoadmap401 | UpdateRoadmap403 | UpdateRoadmap404 | UpdateRoadmap500>

    /**
 * @summary 로드맵 수정
 */
export const useUpdateRoadmap = <TError = AxiosError<UpdateRoadmap400 | UpdateRoadmap401 | UpdateRoadmap403 | UpdateRoadmap404 | UpdateRoadmap500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateRoadmap>>, TError,{roadmapId: number;data: UpdateRoadmapBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof updateRoadmap>>,
        TError,
        {roadmapId: number;data: UpdateRoadmapBody},
        TContext
      > => {

      const mutationOptions = getUpdateRoadmapMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 특정 로드맵을 삭제합니다. 로드맵 소유자만 삭제할 수 있습니다.
 * @summary 로드맵 삭제
 */
export const deleteRoadmap = (
    roadmapId: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<void>> => {
    
    return axios.delete(
      `https://api.ugot.uk/api/roadmaps/${roadmapId}`,options
    );
  }



export const getDeleteRoadmapMutationOptions = <TError = AxiosError<DeleteRoadmap401 | DeleteRoadmap403 | DeleteRoadmap404 | DeleteRoadmap500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteRoadmap>>, TError,{roadmapId: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof deleteRoadmap>>, TError,{roadmapId: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteRoadmap>>, {roadmapId: number}> = (props) => {
          const {roadmapId} = props ?? {};

          return  deleteRoadmap(roadmapId,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteRoadmapMutationResult = NonNullable<Awaited<ReturnType<typeof deleteRoadmap>>>
    
    export type DeleteRoadmapMutationError = AxiosError<DeleteRoadmap401 | DeleteRoadmap403 | DeleteRoadmap404 | DeleteRoadmap500>

    /**
 * @summary 로드맵 삭제
 */
export const useDeleteRoadmap = <TError = AxiosError<DeleteRoadmap401 | DeleteRoadmap403 | DeleteRoadmap404 | DeleteRoadmap500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteRoadmap>>, TError,{roadmapId: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof deleteRoadmap>>,
        TError,
        {roadmapId: number},
        TContext
      > => {

      const mutationOptions = getDeleteRoadmapMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 현재 로그인한 사용자가 특정 로드맵의 구독을 취소합니다.
 * @summary 로드맵 구독 취소
 */
export const unsubscribeRoadmap = (
    roadmapId: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<void>> => {
    
    return axios.delete(
      `https://api.ugot.uk/api/roadmaps/${roadmapId}/subscription`,options
    );
  }



export const getUnsubscribeRoadmapMutationOptions = <TError = AxiosError<UnsubscribeRoadmap401 | UnsubscribeRoadmap403 | UnsubscribeRoadmap404 | UnsubscribeRoadmap500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unsubscribeRoadmap>>, TError,{roadmapId: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof unsubscribeRoadmap>>, TError,{roadmapId: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unsubscribeRoadmap>>, {roadmapId: number}> = (props) => {
          const {roadmapId} = props ?? {};

          return  unsubscribeRoadmap(roadmapId,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UnsubscribeRoadmapMutationResult = NonNullable<Awaited<ReturnType<typeof unsubscribeRoadmap>>>
    
    export type UnsubscribeRoadmapMutationError = AxiosError<UnsubscribeRoadmap401 | UnsubscribeRoadmap403 | UnsubscribeRoadmap404 | UnsubscribeRoadmap500>

    /**
 * @summary 로드맵 구독 취소
 */
export const useUnsubscribeRoadmap = <TError = AxiosError<UnsubscribeRoadmap401 | UnsubscribeRoadmap403 | UnsubscribeRoadmap404 | UnsubscribeRoadmap500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unsubscribeRoadmap>>, TError,{roadmapId: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof unsubscribeRoadmap>>,
        TError,
        {roadmapId: number},
        TContext
      > => {

      const mutationOptions = getUnsubscribeRoadmapMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 현재 로그인한 사용자가 특정 로드맵을 구독합니다.
 * @summary 로드맵 구독
 */
export const subscribeRoadmap = (
    roadmapId: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<void>> => {
    
    return axios.post(
      `https://api.ugot.uk/api/roadmaps/${roadmapId}/subscription`,undefined,options
    );
  }



export const getSubscribeRoadmapMutationOptions = <TError = AxiosError<SubscribeRoadmap401 | SubscribeRoadmap403 | SubscribeRoadmap404 | SubscribeRoadmap409 | SubscribeRoadmap500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof subscribeRoadmap>>, TError,{roadmapId: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof subscribeRoadmap>>, TError,{roadmapId: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof subscribeRoadmap>>, {roadmapId: number}> = (props) => {
          const {roadmapId} = props ?? {};

          return  subscribeRoadmap(roadmapId,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type SubscribeRoadmapMutationResult = NonNullable<Awaited<ReturnType<typeof subscribeRoadmap>>>
    
    export type SubscribeRoadmapMutationError = AxiosError<SubscribeRoadmap401 | SubscribeRoadmap403 | SubscribeRoadmap404 | SubscribeRoadmap409 | SubscribeRoadmap500>

    /**
 * @summary 로드맵 구독
 */
export const useSubscribeRoadmap = <TError = AxiosError<SubscribeRoadmap401 | SubscribeRoadmap403 | SubscribeRoadmap404 | SubscribeRoadmap409 | SubscribeRoadmap500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof subscribeRoadmap>>, TError,{roadmapId: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof subscribeRoadmap>>,
        TError,
        {roadmapId: number},
        TContext
      > => {

      const mutationOptions = getSubscribeRoadmapMutationOptions(options);

      return useMutation(mutationOptions);
    }
    